/**
 * ExpertProfileTab – MVP-style section-based expert profile editor.
 * Used as the main editing UI within the Expert Profiles module.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import * as epApi from './api';
import { SkillsSection } from './sections/SkillsSection';
import { ExperienceSection } from './sections/ExperienceSection';
import { LanguagesSection } from './sections/LanguagesSection';
import { ProjectsSection } from './sections/ProjectsSection';
import { CertificationsSection } from './sections/CertificationsSection';
import { AttachmentsSection } from './sections/AttachmentsSection';
import type { ExpertProfile, SkillItem } from './types';
import styles from './ExpertProfileTab.module.css';

interface ExpertProfileTabProps {
  apiBase?: string;
}

export function ExpertProfileTab({ apiBase = '/expert-profile/me' }: ExpertProfileTabProps) {
  const [profile, setProfile] = useState<ExpertProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get<ExpertProfile>(apiBase);
      setProfile(data);
      setError('');
    } catch {
      setError('Experten-Profil konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleTogglePublish = async () => {
    if (!profile) return;
    setPublishing(true);
    try {
      await epApi.setMyPublished(!profile.isPublished);
      await loadProfile();
    } catch {
      setError('Veröffentlichungsstatus konnte nicht geändert werden');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        Experten-Profil wird geladen...
      </div>
    );
  }

  if (error && !profile) {
    return <div className={styles.errorBox}>{error}</div>;
  }

  if (!profile) return null;

  return (
    <div className={styles.expertContainer}>
      {/* Veröffentlichungs-Status */}
      <div className={styles.publishBar}>
        <div className={styles.publishStatus}>
          <span
            className={
              profile.isPublished ? styles.publishDotActive : styles.publishDotInactive
            }
          />
          <span className={styles.publishLabel}>
            {profile.isPublished
              ? 'Profil ist veröffentlicht — sichtbar in der Profilübersicht'
              : 'Profil ist nicht veröffentlicht — nur für Sie sichtbar'}
          </span>
        </div>
        <button
          type="button"
          className={
            profile.isPublished ? styles.unpublishBtn : styles.publishBtn
          }
          onClick={handleTogglePublish}
          disabled={publishing}
        >
          {publishing
            ? 'Wird gespeichert...'
            : profile.isPublished
              ? 'Zurückziehen'
              : 'Veröffentlichen'}
        </button>
      </div>

      {/* Skills + Sprachen + Erfahrungen nebeneinander */}
      <div className={styles.threeColumnRow}>
        <SkillsSection skills={profile.skills.map(s => typeof s === 'string' ? { name: s } as SkillItem : s)} onUpdate={loadProfile} apiBase={apiBase} />
        <LanguagesSection languages={profile.languages} onUpdate={loadProfile} apiBase={apiBase} />
        <ExperienceSection experiences={profile.experiences} onUpdate={loadProfile} apiBase={apiBase} />
      </div>
      <ProjectsSection projects={profile.projects} onUpdate={loadProfile} apiBase={apiBase} />
      {/* Zertifizierungen + Profilanlagen nebeneinander */}
      <div className={styles.twoColumnRow}>
        <CertificationsSection certifications={profile.certifications} onUpdate={loadProfile} apiBase={apiBase} />
        <AttachmentsSection attachments={profile.attachments} onUpdate={loadProfile} apiBase={apiBase} />
      </div>
    </div>
  );
}
